import { useState } from 'react';
import axios from 'axios';

/**
 * Custom hook for user management functionality
 * Handles user CRUD operations and form state
 */
export const useUserManagement = () => {
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    company_name: '',
    phone_number: '',
    password: ''
  });
  const [isDeleting, setIsDeleting] = useState(false); // Prevent duplicate delete requests
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Create user
  const createUser = async (userData) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/users`,
        userData,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      if (response.status === 201) {
        setShowAddUser(false);
        setFormData({
          email: '',
          full_name: '',
          company_name: '',
          phone_number: '',
          password: ''
        });
        return { success: true, message: 'User created successfully' };
      } else {
        return { success: false, message: `Unexpected response status: ${response.status}` };
      }
    } catch (error) {
      console.error('Error creating user:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error creating user' 
      };
    }
  };
  
  // Update user
  const updateUser = async (userId, userData) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/users/${userId}`,
        userData,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      if (response.status === 200) {
        setEditingUser(null);
        setFormData({
          email: '',
          full_name: '',
          company_name: '',
          phone_number: '',
          password: ''
        });
        return { success: true, message: 'User updated successfully' };
      } else {
        return { success: false, message: `Unexpected response status: ${response.status}` };
      }
    } catch (error) {
      console.error('Error updating user:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error updating user' 
      };
    }
  };
  
  // Delete user
  const deleteUser = async (userId) => {
    if (isDeleting) {
      return { success: false, message: 'Delete operation already in progress' };
    }
    
    try {
      setIsDeleting(true);
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.delete(
        `${API_BASE_URL}/api/admin/users/${userId}`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      if (response.status === 200) {
        return { success: true, message: 'User deleted successfully' };
      } else {
        return { success: false, message: `Unexpected response status: ${response.status}` };
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error deleting user' 
      };
    } finally {
      setIsDeleting(false);
    }
  };

  // Deactivate user
  const deactivateUser = async (userId, reason = '') => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/users/${userId}/deactivate`,
        { reason },
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      if (response.status === 200) {
        return { success: true, message: 'User deactivated successfully' };
      } else {
        return { success: false, message: `Unexpected response status: ${response.status}` };
      }
    } catch (error) {
      console.error('Error deactivating user:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error deactivating user' 
      };
    }
  };

  // Activate user
  const activateUser = async (userId) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/users/${userId}/activate`,
        {},
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      if (response.status === 200) {
        return { success: true, message: 'User activated successfully' };
      } else {
        return { success: false, message: `Unexpected response status: ${response.status}` };
      }
    } catch (error) {
      console.error('Error activating user:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error activating user' 
      };
    }
  };

  // Assign role to user (SUPERADMIN only)
  const assignUserRole = async (userId, role, adminRole = null, reason = '') => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/users/${userId}/assign-role`,
        { 
          role, 
          admin_role: adminRole, 
          reason 
        },
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      if (response.status === 200) {
        return { success: true, message: 'User role assigned successfully' };
      } else {
        return { success: false, message: `Unexpected response status: ${response.status}` };
      }
    } catch (error) {
      console.error('Error assigning user role:', error);
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Error assigning user role' 
      };
    }
  };
  
  return {
    showAddUser,
    setShowAddUser,
    editingUser,
    setEditingUser,
    formData,
    setFormData,
    isDeleting,
    createUser,
    updateUser,
    deleteUser,
    deactivateUser,
    activateUser,
    assignUserRole
  };
};